import { useState } from 'react'
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native'
import { authClient, signOut, useSession } from '../auth'
import { AppealOutcomeForm } from '../components/AppealOutcomeForm'
import { ErrorNotice } from '../components/ErrorNotice'
import { formatGBPFromPence } from '../format'
import { colors, fontFamily } from '../tokens'
import { trpc } from '../trpc'

type Props = {
  onSignedOut: () => void
}

export const AccountScreen = ({ onSignedOut }: Props) => {
  const session = useSession()
  const me = trpc.account.me.useQuery()
  const emailVerified = me.data?.emailVerified === true
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const myAppeals = trpc.account.myAppeals.useQuery(undefined, {
    enabled: Boolean(session.data?.user) && emailVerified,
  })
  const deleteAccount = trpc.account.deleteMyAccount.useMutation({
    onSuccess: async () => {
      await signOut()
      onSignedOut()
    },
  })

  const resendVerification = async () => {
    if (!me.data?.email) return
    setResendStatus('sending')
    try {
      await authClient.sendVerificationEmail({ email: me.data.email })
      setResendStatus('sent')
    } catch {
      setResendStatus('error')
    }
  }

  const onDelete = () => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        'Delete your account? This will remove your name and email from all stored records. Audit logs are kept for 6 years per UK GDPR. This cannot be undone.',
      )
    ) {
      return
    }
    deleteAccount.mutate()
  }

  if (!me.data) {
    return (
      <View style={styles.container}>
        <Text style={styles.subtitle}>Loading…</Text>
      </View>
    )
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Text style={styles.title}>Hi, {me.data.name ?? me.data.email}</Text>

      <Text style={styles.label}>Email</Text>
      <Text style={styles.value}>{me.data.email}</Text>

      {!emailVerified ? (
        <View style={styles.verifyBanner}>
          <Text style={styles.verifyTitle}>Confirm your email</Text>
          <Text style={styles.verifySubtitle}>
            We sent a confirmation link to {me.data.email}. Click it to unlock your saved appeals.
          </Text>
          <Pressable
            accessibilityRole="button"
            disabled={resendStatus === 'sending'}
            onPress={resendVerification}
            style={[styles.verifyButton, resendStatus === 'sending' && styles.disabled]}
          >
            <Text style={styles.verifyButtonText}>
              {resendStatus === 'sending'
                ? 'Sending…'
                : resendStatus === 'sent'
                  ? 'Sent — check your inbox'
                  : resendStatus === 'error'
                    ? 'Send failed — try again'
                    : 'Resend verification email'}
            </Text>
          </Pressable>
        </View>
      ) : null}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Your appeals</Text>
        {!emailVerified ? (
          <Text style={styles.subtitle}>
            Verify your email above to see appeals you've created.
          </Text>
        ) : myAppeals.isLoading ? (
          <Text style={styles.subtitle}>Loading…</Text>
        ) : !myAppeals.data || myAppeals.data.length === 0 ? (
          <Text style={styles.subtitle}>
            You don't have any appeals yet. Run a band check from the home screen — if your email
            matches what you used here, the appeal will show up automatically.
          </Text>
        ) : (
          myAppeals.data.map((appeal) => {
            const awaitingOutcome =
              appeal.status === 'SENT_TO_VOA' && appeal.decisionOutcome == null
            return (
              <View key={appeal.id} style={styles.appealRow}>
                <Text style={styles.appealAddress}>{appeal.bandCheck.property.addressLine1}</Text>
                <Text style={styles.appealMeta}>
                  {appeal.bandCheck.property.postcode} · current band{' '}
                  {appeal.bandCheck.property.currentBand} → suggested{' '}
                  {appeal.bandCheck.suggestedBand}
                </Text>
                <Text style={styles.appealMeta}>
                  Status: {appeal.status} · Path: {appeal.submissionPath}
                  {appeal.decisionOutcome ? ` · ${appeal.decisionOutcome}` : ''}
                </Text>
                <Text style={styles.appealMeta}>
                  Reserved at {formatGBPFromPence(appeal.priceAmountPence)} on{' '}
                  {new Date(appeal.createdAt).toLocaleDateString('en-GB')}
                </Text>
                {awaitingOutcome ? (
                  <AppealOutcomeForm
                    appealId={appeal.id}
                    onSubmitted={() => myAppeals.refetch()}
                    variant="inline"
                  />
                ) : null}
              </View>
            )
          })
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Account</Text>
        <Pressable
          accessibilityRole="button"
          onPress={async () => {
            await signOut()
            onSignedOut()
          }}
          style={styles.secondaryButton}
        >
          <Text style={styles.secondaryButtonText}>Sign out</Text>
        </Pressable>
        <Pressable accessibilityRole="button" onPress={onDelete} style={styles.dangerButton}>
          <Text style={styles.dangerButtonText}>
            {deleteAccount.isPending ? 'Deleting…' : 'Delete account'}
          </Text>
        </Pressable>
        <ErrorNotice error={deleteAccount.error} />
      </View>
    </ScrollView>
  )
}

// The "Confirm your email" banner is a deliberate warm caution state. The
// Meridian token palette is cool indigo + mint with red reserved for errors,
// so it has no amber slot — these warm hexes are intentionally kept outside
// the token set to read as a (non-error) caution prompt.
const amberCaution = {
  bg: '#fff7ed',
  border: '#fed7aa',
  title: '#7c2d12',
  body: '#9a3412',
  button: '#c2410c',
} as const

const styles = StyleSheet.create({
  container: { padding: 24, gap: 8 },
  title: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 12,
    color: colors.ink,
    fontFamily: fontFamily.body,
  },
  label: {
    fontSize: 12,
    color: colors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontFamily: fontFamily.body,
  },
  value: { fontSize: 15, marginBottom: 8, color: colors.ink, fontFamily: fontFamily.body },
  subtitle: { fontSize: 14, color: colors.muted, fontFamily: fontFamily.body },
  section: { marginTop: 24, gap: 12 },
  sectionTitle: { fontSize: 18, fontWeight: '700', color: colors.ink, fontFamily: fontFamily.body },
  appealRow: {
    padding: 12,
    backgroundColor: colors.surface2,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    gap: 4,
  },
  appealAddress: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.ink,
    fontFamily: fontFamily.body,
  },
  appealMeta: { fontSize: 12, color: colors.muted, fontFamily: fontFamily.body },
  verifyBanner: {
    marginTop: 12,
    padding: 14,
    borderRadius: 10,
    backgroundColor: amberCaution.bg,
    borderWidth: 1,
    borderColor: amberCaution.border,
    gap: 8,
  },
  verifyTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: amberCaution.title,
    fontFamily: fontFamily.body,
  },
  verifySubtitle: { fontSize: 13, color: amberCaution.body, fontFamily: fontFamily.body },
  verifyButton: {
    marginTop: 4,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: amberCaution.button,
    alignItems: 'center',
  },
  verifyButtonText: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '600',
    fontFamily: fontFamily.body,
  },
  disabled: { opacity: 0.5 },
  secondaryButton: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.ink,
    fontFamily: fontFamily.body,
  },
  dangerButton: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.error[700],
    backgroundColor: colors.error[100],
    alignItems: 'center',
  },
  dangerButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.error[700],
    fontFamily: fontFamily.body,
  },
  error: { color: colors.error[700], fontSize: 13, fontFamily: fontFamily.body },
})
